import { formatDateTime, twMerge } from '@belong/common';
import { ButtonBase, Image, Text } from '@belong/ui';
import type { MaintenanceRecurrenceModel } from 'api/models';
import Label from 'components/Label/Label';
import Money from 'components/Money/Money';
import { startCase } from 'es-toolkit/compat';
import { STRINGS } from 'maintenance/strings';
import type { ProServiceView } from 'maintenance/types';
import {
  getFormattedFrequency,
  getRecurringTaskDateByStatus,
  getRecurringTaskImageByRecurringTask,
  getRecurringTaskPricing,
  getRecurringTaskStatus,
} from 'maintenance/utils';
import styles from './recurring-task-item.module.css';

type Props = {
  onClick: (task: MaintenanceRecurrenceModel) => () => void;
  task: MaintenanceRecurrenceModel;
  view?: ProServiceView;
};

export function RecurringTaskItem({
  onClick,
  task,
  view = 'homeOwner',
}: Props) {
  const imageSrc = getRecurringTaskImageByRecurringTask(task.recurringTask);
  const pricing = getRecurringTaskPricing({ task, view });

  return (
    <ButtonBase
      className={twMerge(
        'rounded border border-gray border-solid flex items-center p-sm hover:bg-light-gray hover:border-navy w-full transition-colors duration-hover',
        styles.wrapper,
      )}
      onClick={onClick(task)}
    >
      <div className="w-full">
        <div className="mb-sm flex w-full flex-col items-start gap-xs border-0 border-b border-dashed border-gray pb-sm md:flex-row md:items-center md:gap-sm">
          <div className="h-[50px] w-[50px] overflow-hidden rounded-small">
            {imageSrc && <Image alt="" src={imageSrc} />}
          </div>

          <div className="text-left md:flex-1">
            <Text className="line-clamp-1" fontWeight="semibold">
              {startCase(task.summary)}
            </Text>
            <Text variant="p1">
              {formatDateTime({
                dateTime: task.createdOn,
                format: 'LLLL do, Y',
              })}
            </Text>
          </div>

          <div className="flex flex-col items-start gap-xs md:flex-row md:items-center">
            {task.isCancelled ? (
              <Label text="Turned Off" color="gray" />
            ) : (
              <Label
                text={`Routine | ${getFormattedFrequency(task.repeatFrequencyDays)}`}
              />
            )}
          </div>
        </div>

        <div className="flex flex-col items-stretch gap-xs text-left md:flex-row md:items-center md:gap-sm">
          <Text variant="p1">
            {typeof pricing === 'number' ? (
              <>
                {STRINGS.est}: &nbsp;
                <b>
                  <Money cash={pricing} />
                </b>
              </>
            ) : (
              <>
                {STRINGS.pricing}:&nbsp;<b>{pricing}</b>
              </>
            )}
          </Text>

          <Text variant="p1">
            {getRecurringTaskStatus({ task })}
            {': '}
            <b>{getRecurringTaskDateByStatus({ task })}</b>
          </Text>
        </div>
      </div>
    </ButtonBase>
  );
}
